import Toggle from 'components/ReusableComponents/Toggle'
import {
  SequenceEndIcon,
  SequencePlusIcon,
  SequenceStartIcon
} from 'features/CustomCampaigns/EditCampaign/CampaignFlowIcons'
import { useState } from 'react'

import DripDelayNode from '../DripDelayNode'
import {
  addFollowUpStep,
  canAddFollowUpStep,
  convertFromHours,
  convertToHours,
  getFollowUpSteps,
  getSequenceStepSubtitle,
  getSequenceStepTitle,
  MAX_FOLLOW_UP_STEPS,
  removeAllFollowUpSteps,
  removeSequenceStep,
  updateSequenceStep
} from '../dripSequenceHelpers'
import DripStepCard from '../DripStepCard'
import SequenceTemplatePickerModal from '../SequenceTemplatePickerModal'
import StepContentEditor, { stepEditorChannels } from '../StepContentEditor'
import styles from './DripSequenceFlow.module.scss'

function DripSequenceFlow({
  steps,
  onStepsChange,
  campaignId,
  campaignName,
  medium,
  stepErrors = {},
  onPersistDraft,
  canAddFollowUps = true
}) {
  const [editingStep, setEditingStep] = useState(null)
  const [templatePickerStepId, setTemplatePickerStepId] = useState(null)
  const [stashedFollowUps, setStashedFollowUps] = useState([])

  const areFollowUpsEnabled = steps.length > 1
  const canAddAnotherFollowUp = canAddFollowUpStep(steps)

  const templatePickerStep = templatePickerStepId
    ? steps.find(step => step.id === templatePickerStepId)
    : null

  const editedStep = editingStep ? steps.find(step => step.id === editingStep.id) : null

  function getStepTitleById(stepId) {
    const index = steps.findIndex(step => step.id === stepId)
    return index >= 0 ? getSequenceStepTitle(steps[index], index) : 'Message'
  }

  function handleDelayChange(stepId, displayValue, unit) {
    onStepsChange(
      updateSequenceStep(steps, stepId, {
        hoursAfterPreviousCampaign: convertToHours(displayValue, unit)
      })
    )
  }

  function handleAddFollowUp() {
    onStepsChange(addFollowUpStep(steps, medium))
  }

  function handleToggleFollowUps(event) {
    if (!event.target.checked) {
      setStashedFollowUps(getFollowUpSteps(steps))
      onStepsChange(removeAllFollowUpSteps(steps))
      return
    }

    onStepsChange(
      stashedFollowUps.length > 0
        ? [...steps, ...stashedFollowUps]
        : addFollowUpStep(steps, medium)
    )
    setStashedFollowUps([])
  }

  function handleRemoveStep(stepId) {
    onStepsChange(removeSequenceStep(steps, stepId))
  }

  function handleClearText(stepId) {
    onStepsChange(
      updateSequenceStep(steps, stepId, {
        messageTemplate: '',
        messageMedia: null,
        isTextContentReviewed: false
      })
    )
  }

  function handleClearEmail(stepId) {
    onStepsChange(
      updateSequenceStep(steps, stepId, {
        emailTemplateId: null,
        emailTemplateName: '',
        emailTemplateDescription: '',
        isEmailContentReviewed: false
      })
    )
  }

  function handleTemplateApplied({ templateId, templateName, templateDescription }) {
    onStepsChange(
      updateSequenceStep(steps, templatePickerStepId, {
        emailTemplateId: templateId,
        emailTemplateName: templateName,
        emailTemplateDescription: templateDescription,
        isEmailContentReviewed: false
      })
    )
    setTemplatePickerStepId(null)
  }

  function handleEditorSave(payload) {
    onStepsChange(updateSequenceStep(steps, editingStep.id, payload))
    setEditingStep(null)
  }

  const handlePersistDraftBeforeTest = onPersistDraft
    ? async payload => {
        const updatedSteps = updateSequenceStep(steps, editingStep.id, payload)
        onStepsChange(updatedSteps)
        await onPersistDraft(updatedSteps)
      }
    : null

  return (
    <div className={styles.wrapper}>
      <div className={styles.scrollArea}>
        <div className={styles.marker}>
          <span className={styles.markerIcon} aria-hidden="true">
            <SequenceStartIcon />
          </span>
          Campaign starts
        </div>

        {steps.map((step, index) => {
          const delay = convertFromHours(step.hoursAfterPreviousCampaign)

          return (
            <div className={styles.stepGroup} key={step.id}>
              {index > 0 && (
                <>
                  <span className={styles.connector} aria-hidden="true" />
                  <DripDelayNode
                    value={delay.displayValue}
                    unit={delay.unit}
                    onChange={(value, unit) => handleDelayChange(step.id, value, unit)}
                  />
                </>
              )}
              <span className={styles.connector} aria-hidden="true" />
              <DripStepCard
                step={step}
                title={getSequenceStepTitle(step, index)}
                subtitle={getSequenceStepSubtitle(step, index)}
                medium={medium}
                errorMessage={stepErrors[step.id]}
                canRemove={index > 0}
                onEditText={() =>
                  setEditingStep({ id: step.id, channel: stepEditorChannels.text })
                }
                onEditEmail={() =>
                  setEditingStep({ id: step.id, channel: stepEditorChannels.email })
                }
                onSelectEmailTemplate={() => setTemplatePickerStepId(step.id)}
                onChangeEmailTemplate={() => setTemplatePickerStepId(step.id)}
                onClearText={() => handleClearText(step.id)}
                onClearEmail={() => handleClearEmail(step.id)}
                onRemove={() => handleRemoveStep(step.id)}
              />
            </div>
          )
        })}

        <span className={styles.connector} aria-hidden="true" />
        {canAddFollowUps && (
          <div className={styles.followUpControls}>
            <div className={styles.followUpToggleRow}>
              <div className={styles.followUpCopy}>
                <span className={styles.followUpTitle}>Send follow-up messages</span>
                <span className={styles.followUpDescription}>
                  Keep nudging guests who did not book after the initial message.
                </span>
              </div>
              <Toggle
                checked={areFollowUpsEnabled}
                handleChange={handleToggleFollowUps}
              />
            </div>
            {areFollowUpsEnabled &&
              (canAddAnotherFollowUp ? (
                <button
                  type="button"
                  className={styles.addFollowUp}
                  onClick={handleAddFollowUp}
                >
                  <SequencePlusIcon />
                  Add follow-up
                </button>
              ) : (
                <p className={styles.followUpLimit}>
                  You can add up to {MAX_FOLLOW_UP_STEPS} follow-ups per campaign.
                </p>
              ))}
          </div>
        )}
        <span className={styles.connector} aria-hidden="true" />

        <div className={styles.marker}>
          <span className={styles.markerIcon} aria-hidden="true">
            <SequenceEndIcon />
          </span>
          Campaign ends
        </div>
      </div>

      {templatePickerStep && (
        <SequenceTemplatePickerModal
          campaignId={templatePickerStep.contentId ?? campaignId}
          campaignName={campaignName}
          stepTitle={getStepTitleById(templatePickerStepId)}
          initialTemplateId={templatePickerStep.emailTemplateId}
          onClose={() => setTemplatePickerStepId(null)}
          onApplied={handleTemplateApplied}
          onPersistDraft={() => onPersistDraft?.(steps)}
        />
      )}

      {editedStep && (
        <StepContentEditor
          key={`${editedStep.id}-${editingStep.channel}`}
          title={getStepTitleById(editedStep.id)}
          medium={medium}
          contentId={editedStep.contentId ?? campaignId}
          initialChannel={editingStep.channel}
          initialData={editedStep}
          isCustomUrlAllowed={Boolean(editedStep.isInitialStep)}
          onClose={() => setEditingStep(null)}
          onSave={handleEditorSave}
          onSelectTemplate={() => {
            setEditingStep(null)
            setTemplatePickerStepId(editedStep.id)
          }}
          onPersistDraft={handlePersistDraftBeforeTest}
        />
      )}
    </div>
  )
}

export default DripSequenceFlow
